import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Button, Paragraph, Select } from '@treams/treams-ui';

import MomentTemplateVariation from './MomentTemplateVariation';
import { MomentTemplateVariationType } from './types';

interface IOption {
	label: string;
	value: string;
}

interface IRoleOption {
	label: string;
	value: graphql.RoleType;
}

export default function MomentTemplateVariations({
	momentType,
	name,
	defaultTemplateName,
	templates,
	jobTitles,
	roles,
}: {
	momentType: MomentTemplateVariationType;
	name: string;
	defaultTemplateName: string;
	templates: IOption[];
	jobTitles: IOption[];
	roles: IRoleOption[];
}) {
	const { t } = useTranslation();

	const [{ value: variations }, , { setValue: setVariations }] = useField<
		graphql.MomentVariation[] | undefined
	>(name);
	const [{ value: defaultTemplateID }, , { setValue: setDefaultTemplateID }] = useField<
		string | undefined
	>(defaultTemplateName);

	const hasConditionSource = jobTitles.length > 0 || roles.length > 0;

	const templateOptions = useMemo(
		() =>
			templates.map(({ value, label }) => ({
				title: label,
				value,
			})),
		[templates],
	);

	const handleDefaultTemplateChange = useCallback(
		(value: string | undefined) => {
			if (!value) {
				return;
			}

			setDefaultTemplateID(value);
		},
		[setDefaultTemplateID],
	);

	const handleAddVariation = useCallback(() => {
		const newVariation: graphql.MomentVariation =
			momentType === graphql.MomentType.Feedback
				? ({
						feedback: {
							feedbackTemplate: {
								id: templates[0].value,
							} as unknown as graphql.FeedbackTemplate,
						},
					} as unknown as graphql.MomentVariation)
				: ({
						evaluation: {
							evaluationForm: {
								id: templates[0].value,
							} as unknown as graphql.EvaluationForm,
						},
					} as unknown as graphql.MomentVariation);

		setVariations((variations ?? []).concat([newVariation]));
	}, [momentType, templates, setVariations, variations]);

	return (
		<Box>
			<Box mb={3}>
				<Box mb={2}>
					<Paragraph>{t('common.settings.momentTemplates.variations.default.title')}</Paragraph>
				</Box>

				<Select
					small
					selectedValue={defaultTemplateID}
					options={templateOptions}
					onChange={handleDefaultTemplateChange}
				/>
			</Box>

			{variations && variations.length > 0 && (
				<Box mt={3}>
					<Box>
						<Paragraph>{t('common.settings.momentTemplates.alternatives.title')}</Paragraph>
					</Box>

					<Box>
						{(variations ?? []).map((variation, index) => (
							<MomentTemplateVariation
								key={index}
								momentType={momentType}
								name={`${name}[${index}]`}
								jobTitles={jobTitles}
								roles={roles}
								templates={templates}
								onRemove={() => {
									setVariations(variations?.filter((_, i) => i !== index));
								}}
							/>
						))}
					</Box>
				</Box>
			)}

			{templates.length > 0 && hasConditionSource && (
				<Flex>
					<Button
						onClick={handleAddVariation}
						title={t('common.settings.momentTemplates.alternatives.button')}
						small
						type="secondary"
					/>
				</Flex>
			)}
		</Box>
	);
}
